<template>
    <div class="w-100 h-100" style="position: relative">
        <div class="poi-select">
            <v-btn
                class="dialog-button"
                style="font-size: 18px!important;font-family: Roboto, Arial, sans-serif!important;color: rgb(86, 86, 86)!important;"
                text
                @click = "onCenter"
            >
                {{ $t("disposition.map.center") }}
            </v-btn>
        </div>

        <div id="inspection-map"
             class = "google-map w-100 h-100"
             ref   = "googleMap"
        >
        </div>
    </div>
</template>

<script>

import {MarkerClusterer} from "@googlemaps/markerclusterer";
import main from "@/main";
import Vue from "vue";
import Core from "@/modules/core/service/Core";
import {toRaw} from 'vue';
import CInspectionMapUnit from "@/modules/dis/components/map/private/CInspectionMapUnit.vue";

const MAP = "inspection-map";

const styles = {
    default: [],
    hide: [
        {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
        },
        {
            featureType: "transit",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
        },
    ],
};

const circle = function (color, assignedToCurrentTech = false, opacity = 1) {
    return window.btoa(`<svg fill="${color}" xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240"><circle cx="120" cy="120" opacity="${0.4 * opacity}" r="110" /><circle cx="120" cy="120" opacity="${opacity}" r="70" stroke="white" stroke-width="5" /></svg>`);
}

const markerPin = function (color, icon = '', opacity = 1) {
    // Icons centered in inner circle (cx="120" cy="80" r="35") - use pin color for contrast
    const iconSvg = icon === 'check' ? 
        `<path d="M 100 80 L 112 92 L 140 64" stroke="${color}" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>` :
        icon === 'person' ?
        `<circle cx="120" cy="72" r="8" fill="${color}"/><path d="M 108 92 Q 108 82 120 82 Q 132 82 132 92" fill="${color}" stroke="${color}" stroke-width="1"/>` :
        '';
    
    return window.btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="240" height="300" viewBox="0 0 240 300"><path d="M 120 10 C 80 10 50 40 50 80 C 50 120 120 200 120 200 C 120 200 190 120 190 80 C 190 40 160 10 120 10 Z" fill="${color}" stroke="white" stroke-width="4" opacity="${opacity}"/><circle cx="120" cy="80" r="35" fill="white" opacity="${opacity * 0.9}"/>${iconSvg}</svg>`);
}

// SVG cache to avoid recreating identical markers
const markerCache = {};

const getCachedMarkerPin = function (color, icon = '', opacity = 1) {
    // Round opacity to 2 decimals to avoid floating point precision issues
    const roundedOpacity = Math.round(opacity * 100) / 100;
    const cacheKey = `${color}-${icon}-${roundedOpacity}`;
    
    if (!markerCache[cacheKey]) {
        markerCache[cacheKey] = markerPin(color, icon, roundedOpacity);
    }
    
    return markerCache[cacheKey];
}

const clusterCache = {};

const getCachedClusterCircle = function (color, assignedToCurrentTech, opacity = 1) {
    const roundedOpacity = Math.round(opacity * 100) / 100;
    const cacheKey = `${color}-${assignedToCurrentTech}-${roundedOpacity}`;
    
    if (!clusterCache[cacheKey]) {
        clusterCache[cacheKey] = circle(color, assignedToCurrentTech, roundedOpacity);
    }
    
    return clusterCache[cacheKey];
}

const getClusterStyle = function (markers, selectedTechnicianId) {
    // Check if any marker is assigned to selected technician
    const assignedToCurrentTech = !selectedTechnicianId || markers.some(marker => 
        marker.unit && marker.unit.technicianId === selectedTechnicianId
    );
    
    // Find the most common order color in the cluster, or use grey as default
    const orderColors = {};
    markers.forEach(marker => {
        if (marker.unit?.order?.color) {
            const color = marker.unit.order.color;
            orderColors[color] = (orderColors[color] || 0) + 1;
        }
    });
    
    let color = "rgb(100, 100, 100)"; // Default grey
    let maxCount = 0;
    Object.entries(orderColors).forEach(([orderColor, count]) => {
        if (count > maxCount) {
            maxCount = count;
            color = orderColor;
        }
    });

    // Use reduced opacity if no technician selected or no units assigned to selected tech
    const opacity = assignedToCurrentTech ? 1 : 0.5;

    return { color, assignedToCurrentTech, opacity };
}

/**
 * Returns the style of a single unit marker based on order color and technician assignment.
 * @param maps 
 * @param unit - the unit object with order and technicianId
 * @param selectedTechnicianId - the currently selected technician ID
 */
const getUnitMarkerStyle = function (maps, unit, selectedTechnicianId) {
    const isAssignedToSelected = !selectedTechnicianId || unit.technicianId === selectedTechnicianId;
    
    // Use order color if available, otherwise default grey
    const color = unit.order?.color || "#90A4AE";
    
    // Show person icon and full opacity if assigned to selected tech
    // Otherwise icon and transparent
    const icon = unit.technicianId ? 'person' : '';
    const opacity = isAssignedToSelected ? 1 : 0.5;
    
    const svg = getCachedMarkerPin(color, icon, opacity);

    return {
        url        : `data:image/svg+xml;base64,${svg}`,
        scaledSize : new maps.Size(60, 75),
        origin     : new maps.Point(0, 0),
        anchor     : new maps.Point(30, 75),
        labelOrigin: new maps.Point(30, 30),
    };
};


/**
 * Adds small random offset to coordinates to prevent exact overlap of markers at same location.
 */
const addCoordinateJitter = function (lat, lng, index) {
    const baseOffset = 0.0002; // ~22 meters - enough visual separation
    const variableOffset = 0.0001; // Add some randomness
    const offset = baseOffset + (Math.random() * variableOffset);
    const angle = (index * 51.4286) % 360; // Golden angle distribution for better spacing
    const rad = (angle * Math.PI) / 180;
    return {
        lat: lat + (offset * Math.cos(rad)),
        lng: lng + (offset * Math.sin(rad))
    };
};



export default {
    name: "CInspectionMap",
    components: {
    },

    props: {
        assignments: {

        },

        technician: {

        },
        
        filteredSites: {
            default: null
        }
    },

    data: () => ({
        sites: [],
        markers: [],
        markerClusters: [],
        lastTechnicianId: null,
        settings: {
            clickableIcons   : true,
            streetViewControl: false,
            panControlOptions: true,
            gestureHandling  : "cooperative",
            mapTypeControl   : true,
            zoom             : 7,
            minZoom          : 2,
            maxZoom          : 18,
            center           : Core.map.centerGermany()
        },

        showSites: true,
        map      : null,
    }),

    computed: {
        google () {
            return window.mgoogle;
        },

        maps () {
            return this.google.maps;
        },
    },

    mounted() {
        window.gotoSite = this.gotoSite;

        this.$nextTick(() => {
            this.load()
        })
    },

    watch: {
        order () {
            this.onFilter();
        },

        assignments () {
            this.onFilter(true);
        },

        technician () {
            this.onFilter();
        },

        filteredSites () {
            this.onFilter();
        }
    },

    methods: {
        load () {
            if (!this.google) {
                return;
            }

            this.onFilter ();
        },

        onCenter () {
            this.onFilter();
        },

        centerOn (coords) {
            if (!this.map || !coords || !coords.lat || !coords.lng) {
                return;
            }
            
            // Create bounds around the location to fit all markers at this location
            const bounds = new this.google.maps.LatLngBounds();
            const center = new this.google.maps.LatLng(coords.lat, coords.lng);
            
            // Find all units at this location and extend bounds
            let foundMarkers = false;
            if (this.sites) {
                this.sites.forEach(assignment => {
                    const unit = assignment.unit;
                    if (unit && unit.latitude && unit.longitude) {
                        const unitLat = parseFloat(unit.latitude);
                        const unitLng = parseFloat(unit.longitude);
                        // Check if unit is at this location (within ~50 meters)
                        if (Math.abs(unitLat - coords.lat) < 0.0005 && Math.abs(unitLng - coords.lng) < 0.0005) {
                            bounds.extend(new this.google.maps.LatLng(unitLat, unitLng));
                            foundMarkers = true;
                        }
                    }
                });
            }
            
            if (foundMarkers) {
                // Fit bounds to show all markers at this location
                this.map.fitBounds(bounds);
            } else {
                // Just center if no markers found
                this.map.panTo(center);
            }
        },

        onFilter (fitBounds) {
            if (!this.map) {
                this.map = new this.maps.Map(document.getElementById(MAP), this.settings);
            }

            // Use filteredSites if filters are active (even if empty), otherwise use all assignments
            if (this.filteredSites !== null) {
                // Flatten filteredUnits from all filtered locations
                this.sites = [];
                this.filteredSites.forEach(location => {
                    if (location.filteredUnits) {
                        location.filteredUnits.forEach(unit => {
                            const assignment = this.assignments.find(a => 
                                String(a.unit.id) === String(unit.id) && !a.done
                            );
                            if (assignment) {
                                this.sites.push(assignment);
                            }
                        });
                    }
                });
            } else {
                this.sites = this.assignments.filter(a => !a.done);
            }
            
            this.onShowSites();

            const bounds = this.loadMarker();
            // Only fit bounds when explicitly requested (fitBounds === true) and bounds are valid
            if (fitBounds === true && bounds && !bounds.isEmpty()) {
                this.map.fitBounds(bounds);
            }
        },

        loadMarker () {
            if (!this.sites) {
                return;
            }

            const currentTechId = this.technician ? this.technician.id : null;
            
            // Only clear caches when technician selection changes
            if (this.lastTechnicianId !== currentTechId) {
                Object.keys(markerCache).forEach(key => delete markerCache[key]);
                Object.keys(clusterCache).forEach(key => delete clusterCache[key]);
                this.lastTechnicianId = currentTechId;
            }

            for (let i = 0; i < this.markerClusters.length; i++) {
                this.markerClusters[i].clearMarkers();
            }

            this.markerClusters = [];
            for (let i = 0; i < this.markers.length; i++) {
                toRaw (this.markers[i]).setMap(null, null);
            }

            this.markers = [];
            const { Marker } = this.google.maps;
            const map        = this.map;
            const infowindow = new this.maps.InfoWindow ();
            const bounds     = new this.google.maps.LatLngBounds();
            const selectedTechId = this.technician ? this.technician.id : null;

            // Group units by location for coordinate jitter
            const locationGroups = {};
            this.sites.forEach(assignment => {
                const unit = assignment.unit;
                unit.technicianName = assignment.technicianName;
                unit.technicianId   = assignment.technicianId;
                
                const key = `${unit.latitude},${unit.longitude}`;
                if (!locationGroups[key]) {
                    locationGroups[key] = [];
                }
                locationGroups[key].push(unit);
            });

            // Create one marker per unit
            Object.values(locationGroups).forEach(units => {
                units.forEach((unit, index) => {
                    if (unit.latitude && unit.longitude) {
                        const isAssignedToSelected = unit.technicianId === selectedTechId;
                        const style = getUnitMarkerStyle(this.maps, unit, selectedTechId);
                        
                        // Add jitter for same-location units
                        const coords = index > 0 ? 
                            addCoordinateJitter(parseFloat(unit.latitude), parseFloat(unit.longitude), index) :
                            { lat: parseFloat(unit.latitude), lng: parseFloat(unit.longitude) };
                        
                        const marker = new Marker({
                            position: coords,

                            state   : {
                                level: isAssignedToSelected ? 2 : 1,
                                color: isAssignedToSelected ? "rgb(76, 175, 80)" : "rgb(100, 100, 100)"
                            },

                            unit: unit, // Store unit reference for cluster rendering

                            icon    : style
                        });

                        bounds.extend(marker.position);

                        const self = this;
                        marker.addListener("click", function () {
                            const html = self.siteCardHtml(unit);
                            infowindow.setContent(html)
                            infowindow.open({
                                anchor: marker,
                                map,
                                shouldFocus: false,
                            });
                        });

                        this.markers.push(marker);
                    }
                });
            });

            const self = this;
            const renderer = {
                render: (clusterData) => self.renderCluster(clusterData, selectedTechId)
            };

            const markers = this.markers;
            const cluster = new MarkerClusterer ({map, markers, renderer  });
            this.markerClusters.push (cluster)

            return bounds;
        },

        renderCluster ({ markers, count, position }, selectedTechnicianId) {
            const { color, assignedToCurrentTech, opacity } = getClusterStyle(markers, selectedTechnicianId);
            const svg = getCachedClusterCircle(color, assignedToCurrentTech, opacity);

            return new this.maps.Marker({
                position,
                icon: {
                    url: `data:image/svg+xml;base64,${svg}`,
                    scaledSize: new this.maps.Size(45, 45),
                },

                label: {
                    text    : String(count),
                    color   : "rgba(255,255,255,0.9)",
                    fontSize: "15px",
                },

                zIndex: Number(this.maps.Marker.MAX_ZINDEX) + count,
            });
        },

        siteCardHtml (unit) {
            const i18n = main.i18n();
            const card = new Vue({...CInspectionMapUnit, i18n, propsData: { unit: unit} });
            const goto = "gotoSite(" + unit.id + ")";
            card.$mount();
            
            // Wrap everything in a container with max-width to prevent horizontal scrollbar
            const wrapper = `
                <div style="min-width: 250px; overflow: hidden;">
                    <div style="padding: 8px 12px; border-bottom: 1px solid #ddd; margin: -8px -8px 8px -8px;">
                        <div style="font-weight: 600; font-size: 14px; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${unit.location?.street || ''}
                        </div>
                        <div style="font-size: 12px; color: #666;">
                            ${unit.location?.zip || ''} ${unit.location?.city || ''}
                        </div>
                    </div>
                    <a onclick="${goto}" style="text-decoration: none; color: inherit;">
                        ${card.$el.outerHTML}
                    </a>
                </div>
            `;
            
            return wrapper;
        },

        onShowSites () {
            this.map.setOptions({ styles: styles[this.showSites ? "hide" : "default"] });
        },

        themeColor (name) {
            return this.$vuetify.theme.themes.light[name];
        },

        gotoSite (unitId) {
            this.$emit("selected", unitId)
        }
    }
}
</script>

<style scoped>

.google-map {
    width: 100%;
    min-height: 100%;
}

.poi-select {
    background-color: white;
    margin-top: 10px;
    height: 40px;
    padding-top: 5px;
    padding-left: 10px;
    padding-right: 10px;
    position: absolute;
    left: 176px;
    z-index: 3;
    /*noinspection CssInvalidFunction*/
    box-shadow: rgb(0 0 0 / 30%) 0 1px 4px -1px;
}

::v-deep div::-webkit-scrollbar {
    width: 19px !important;
}

</style>